export interface TimelineItem {
  date: string;
  title: string;
  detail: string;
}

export interface KeyFigure {
  name: string;
  role: string;
  note: string;
}

export interface Doubt {
  question: string;
  analysis: string;
}

export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface Article {
  id: string;
  slug: string;
  title: string;
  summary: string;
  category: string;
  categorySlug: string;
  cover: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  heat: number;
  featured?: boolean;
  intro: string[];
  timeline: TimelineItem[];
  keyFigures: KeyFigure[];
  doubts: Doubt[];
  analysis: ArticleSection[];
  conclusion: string;
}

export const articles: Article[] = [
  {
    id: "a01",
    slug: "live-stream-accident-three-way-game",
    title: "一场直播事故背后的三重博弈",
    summary:
      "一次看似偶然的直播翻车，为什么在三天内演变成品牌、主播与平台三方各说各话的局面？我们把它拆成可以核对的时间线。",
    category: "网络热点",
    categorySlug: "internet",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20cream%20paper%20texture%20and%20deep%20crimson%20brush%20marks%2C%20abstract%20broadcast%20spotlight%20geometry%2C%20minimal%20investigative%20magazine%20mood%2C%20soft%20studio%20lighting%2C%20flat%20collage%20style%2C%20no%20text&width=800&height=600&seq=art-01-live-15&orientation=landscape",
    author: "编辑部 · 深度组",
    date: "2026-09-08",
    readTime: "9 分钟",
    tags: ["直播事故", "三方声明", "品牌合作"],
    heat: 9862,
    featured: true,
    intro: [
      "事情的起点其实很小：一场品牌专场直播在进行到第 40 分钟时，主播口播的产品参数与商品详情页出现了不一致。画面里停顿了几秒，随后主播把这个环节跳过，直播正常结束。当晚，这段十几秒的片段被单独剪出来传播。",
      "真正让它变成公共事件的，是接下来的三天里，品牌方、主播团队与平台方先后发布的三份声明。三份声明都用了「误解」「沟通不畅」这类词，但彼此之间对「谁先确认口径」这一点的描述并不一致。我们把能查证到的节点按顺序排开，很多争论其实在排序之后就能看清。",
    ],
    timeline: [
      {
        date: "09-05 20:14",
        title: "直播中出现参数分歧",
        detail: "主播口播的续航数据与商品详情页标注不一致，画面停顿约 6 秒后跳过该环节。",
      },
      {
        date: "09-05 23:40",
        title: "切片开始传播",
        detail: "一段约 14 秒的片段被多个账号转发，配文以「翻车」为主，此时品牌方尚未回应。",
      },
      {
        date: "09-06 11:20",
        title: "品牌方发布第一份说明",
        detail: "品牌方称参数以详情页为准，主播表述属「口误」，并强调双方合作仍在继续。",
      },
      {
        date: "09-07 09:05",
        title: "主播团队回应",
        detail: "主播方表示直播前的产品资料并非由自己最终确认，且未收到书面的参数核对清单。",
      },
      {
        date: "09-07 18:30",
        title: "平台方补充说明",
        detail: "平台称已介入协调，商品链接暂时维持原状，未对任一方做违规认定。",
      },
    ],
    keyFigures: [
      {
        name: "主播 A（匿名）",
        role: "直播执行方",
        note: "在事件中承担了最直接的曝光压力，其团队的核心诉求是澄清「资料确认」环节的责任归属。",
      },
      {
        name: "品牌方运营负责人",
        role: "品牌侧对接人",
        note: "负责直播前的产品资料整理与审核，第一份声明出自该团队。",
      },
      {
        name: "平台直播运营",
        role: "平台协调角色",
        note: "声明措辞克制，既未认定违规，也未明确责任，是三方中最晚表态的一方。",
      },
    ],
    doubts: [
      {
        question: "直播前的产品资料到底由谁最终确认？",
        analysis:
          "品牌方称已提供资料，主播方称未收到书面核对清单。目前公开信息里，双方都没有展示出带有确认节点的文件，这是整个事件最核心的证据缺口。",
      },
      {
        question: "为什么切片传播的速度远远快于品牌方回应？",
        analysis:
          "品牌方首份说明距切片传播仅约 11 小时，看似不慢，但传播方的配文已经先一步为事件定了调，后续澄清一直在追赶这个既定印象。",
      },
      {
        question: "平台为何没有做违规认定？",
        analysis:
          "参数不一致属于商品信息层面的问题，而责任归属未明，平台选择协调而非裁决，在流程上是稳妥的，但也让公众等不到一个「结论」。",
      },
    ],
    analysis: [
      {
        heading: "这不是一次口误，而是一次流程断层",
        paragraphs: [
          "把三份声明叠在一起看，会发现三方其实都在描述同一件事的不同侧面：品牌方关注的是「参数以谁为准」，主播方关注的是「资料由谁确认」，平台关注的是「链接要不要下架」。三者并不冲突，但都回避了最关键的交接点。",
          "直播电商的流程里，产品资料通常要经过整理、审核、交付、口播确认四个环节。这次事件暴露的不是某个环节做错了，而是环节之间的交接没有留下可追溯的记录。没有记录，责任就成了各说各话。",
        ],
      },
      {
        heading: "沉默的时间差，才是舆论放大的燃料",
        paragraphs: [
          "从切片传播到品牌方首次回应，中间有约 11 小时的空白。这段时间里，事件的解释权完全交给了传播者。等到各方开始澄清，公众已经先接受了「翻车」这个框架，后续所有说明都被读成「公关」。",
        ],
      },
    ],
    conclusion:
      "如果只看结论，这更像一次流程管理事故，而不是一次人格层面的翻车。它留下的真正问题是：当一次合作的交接没有任何书面确认，一旦出现分歧，谁来证明当时约定了什么。这个问题不解决，类似的剧本还会重演。",
  },
  {
    id: "a02",
    slug: "consumer-brand-three-years",
    title: "估值百亿到悄然退场：一个新消费品牌的三年沉浮",
    summary:
      "从门店排长队到核心商圈撤店，只用了不到两年。复盘「漾白」的扩张节奏，我们找到了三个被忽略的转折点。",
    category: "商业风云",
    categorySlug: "business",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20in%20warm%20beige%20tones%20with%20burgundy%20geometric%20shapes%20and%20abstract%20store%20front%20silhouette%2C%20clean%20investigative%20magazine%20collage%2C%20soft%20daylight%2C%20flat%20graphic%20composition%2C%20no%20text&width=800&height=600&seq=art-02-brand-15&orientation=landscape",
    author: "编辑部 · 商业组",
    date: "2026-08-30",
    readTime: "11 分钟",
    tags: ["新消费", "门店铺张", "融资节奏"],
    heat: 8640,
    featured: true,
    intro: [
      "「漾白」曾经是新消费叙事里最标准的样本：首店选址一线核心商圈，开业三个月拿下区域好评榜第一，一年内完成两轮融资，估值一度被传接近百亿量级。它的门店设计甚至被写进了不少行业复盘。",
      "转折发生在第二年的下半年。先是部分门店调整营业面积，随后几个核心商圈的铺位陆续转让。到第三年，公开渠道已经很难找到它的新动态。我们按时间顺序梳理了它可查证的扩张与收缩节点。",
    ],
    timeline: [
      {
        date: "第一年 Q1",
        title: "首店开业，口碑爆发",
        detail: "首店进入一线核心商圈，靠单品口碑在社交平台形成自传播，三个月内登上区域榜单首位。",
      },
      {
        date: "第一年 Q4",
        title: "完成两轮融资，启动扩张",
        detail: "对外公布的门店计划是在一年内进入 12 座城市，同时开始搭建中央供应链。",
      },
      {
        date: "第二年 Q2",
        title: "门店数量达到峰值",
        detail: "门店数量达到历史最高点，但同期单店坪效开始出现环比下滑，未对外披露具体数字。",
      },
      {
        date: "第二年 Q4",
        title: "核心商圈铺位开始转让",
        detail: "数家核心商圈门店调整或转让，官方口径为「优化门店结构」。",
      },
      {
        date: "第三年 Q1",
        title: "公开动态明显减少",
        detail: "社交账号更新频率下降，未再公布新的融资或开店信息。",
      },
    ],
    keyFigures: [
      {
        name: "创始人团队",
        role: "品牌决策方",
        note: "主导了从单店口碑到快速多城扩张的关键决策，公开表态集中在产品与生活方式层面，较少谈及门店财务。",
      },
      {
        name: "供应链负责人",
        role: "扩张支撑角色",
        note: "负责中央供应链搭建，是决定「开得快」能否走得通的核心岗位，但相关信息始终未对外详细披露。",
      },
      {
        name: "早期投资人",
        role: "资本侧角色",
        note: "在完成第二轮融资后极少公开发声，退出节奏与门店收缩的时间点存在接近之处。",
      },
    ],
    doubts: [
      {
        question: "从「单店排队」到「门店收缩」，中间发生了什么？",
        analysis:
          "公开信息里，单店坪效下滑出现在门店数量达到峰值之后。也就是说，问题不是需求突然消失，而是扩张速度超过了单店的实际承载能力，这一点与「需求消失」的常见解释并不吻合。",
      },
      {
        question: "供应链是否跟上了扩张节奏？",
        analysis:
          "中央供应链的搭建通常需要的时间，明显长于开店的周期。门店先行、供应链后补的顺序，是很多新消费品牌会遇到的结构性风险，但目前缺乏可核实的运营数据。",
      },
      {
        question: "「优化门店结构」到底意味着什么？",
        analysis:
          "这是一个偏中性的官方表述。它既可能是主动的战略收缩，也可能是被动的成本压力，公开信息不足以区分两者，因此我们把它放进疑点而不是写进结论。",
      },
    ],
    analysis: [
      {
        heading: "真正的分水岭，是节奏而不是产品",
        paragraphs: [
          "漾白的产品在首店阶段是被验证过的，口碑爆发本身就是证据。问题出现在验证完成之后：把单店的成功当成了可复制的公式，于是把开店数量当成了增长本身。",
          "当门店从 4 家变成 40 家，考验的就不再是产品，而是供应链、人员培训、选址判断和现金流管理。这四件事，任何一件没跟上，扩张都会变成负担。",
        ],
      },
      {
        heading: "新消费叙事的共同结构",
        paragraphs: [
          "把漾白放进同类案例里看，会发现结构高度相似：口碑起步、资本加速、门店冲量、坪效回落、悄然收缩。它未必是某一家公司的失败，更像是这套叙事在特定阶段会反复出现的形态。",
        ],
      },
    ],
    conclusion:
      "漾白的故事里，最值得记住的不是估值数字，而是那个被忽略的时点——单店坪效开始下滑的那一刻。很多公司的走向，都不取决于它跑得多快，而取决于它在速度与承载力之间做了什么选择。",
  },
  {
    id: "a03",
    slug: "why-video-was-removed-in-three-days",
    title: "热搜第一的那条视频，为什么三天后被撤下",
    summary:
      "一条登上热搜榜首的视频，在三天内从全网可见变成无法访问。我们梳理了它从发布到撤下的完整链条。",
    category: "网络热点",
    categorySlug: "internet",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20cream%20paper%20and%20amber%20accent%20shapes%2C%20abstract%20video%20frame%20geometry%20with%20crimson%20line%20crossing%2C%20minimal%20investigative%20magazine%20mood%2C%20soft%20lighting%2C%20flat%20collage%20style%2C%20no%20text&width=800&height=600&seq=art-03-video-15&orientation=landscape",
    author: "编辑部 · 网络组",
    date: "2026-08-24",
    readTime: "8 分钟",
    tags: ["热搜", "内容治理", "传播链路"],
    heat: 9210,
    featured: false,
    intro: [
      "那条视频最早出现在一个小范围的兴趣社区，最初只有几十条讨论。真正让它起飞的是被搬运到主流短视频平台之后的 6 小时，播放量从四位数跳到七位数，并迅速登上热搜榜首。",
      "第三天，原视频链接开始在不同平台陆续失效。奇怪的是，发布者账号本身并没有被封禁，其他关联内容也大多还在。我们试着还原这段撤下过程里，究竟有哪些节点在起作用。",
    ],
    timeline: [
      {
        date: "D1 上午",
        title: "原始视频在小社区发布",
        detail: "发布者以「记录」为标题发布，初始讨论量很小，评论以核实信息的疑问为主。",
      },
      {
        date: "D1 下午",
        title: "被搬运至主流平台",
        detail: "多个账号转载并重新剪辑，配文语气明显强于原视频，播放量迅速攀升。",
      },
      {
        date: "D1 深夜",
        title: "登上热搜榜首",
        detail: "话题进入榜单第一，讨论焦点从事件本身转向对相关方的情绪表达。",
      },
      {
        date: "D2 全天",
        title: "多方回应与信息对冲",
        detail: "相关方陆续发声，部分平台对话题页做了内容折叠处理。",
      },
      {
        date: "D3",
        title: "原链接失效",
        detail: "原视频链接在多个平台无法访问，但发布者账号与部分二次剪辑版本仍可查。",
      },
    ],
    keyFigures: [
      {
        name: "原始发布者",
        role: "内容起点",
        note: "账号未被封禁，说明处理对象更可能是单条内容而非发布者本身。",
      },
      {
        name: "搬运账号群体",
        role: "传播放大器",
        note: "通过重新剪辑与配文改变了内容的语气与指向，是传播量与情绪同时放大的关键。",
      },
      {
        name: "平台内容审核",
        role: "处置执行方",
        note: "在回应之后对单条内容做处置，而非全站处理，操作方式相对克制。",
      },
    ],
    doubts: [
      {
        question: "为什么处理的是单条内容而不是账号？",
        analysis:
          "账号仍在、其他内容仍在，说明处置的触发条件更可能来自这条内容本身，而非发布者身份。这与「封号」式的处置逻辑明显不同。",
      },
      {
        question: "真正的转折发生在搬运环节还是回应环节？",
        analysis:
          "从数据看，播放量跃升发生在搬运之后，而处置发生在回应之后。两个节点中间隔了整整一天，这段时间里信息已经被重写了好几轮。",
      },
      {
        question: "撤下是否等于认定内容不实？",
        analysis:
          "不能直接画等号。内容处置的原因可能包含版权、隐私、误导性剪辑等多种情形，公开信息并未说明具体依据，我们不做推断。",
      },
    ],
    analysis: [
      {
        heading: "热搜不是起点，搬运才是",
        paragraphs: [
          "很多讨论默认「热搜 = 事件爆发」，但这条视频的轨迹显示，真正的转折点是搬运环节。原视频的语境是克制的，而搬运后的版本需要更强的标题来获取点击，情绪就是在这一步被加上去的。",
          "这也解释了为什么发布者本身没有被处理：被改变的并不是原始内容，而是它被讲述的方式。",
        ],
      },
      {
        heading: "处置的时点，决定了舆论的解读",
        paragraphs: [
          "处置发生在回应之后，而不是之前。这个顺序让一部分人把它读成「压制」，但从中性的流程角度看，更可能是「先有回应、再评估内容」的常规路径。时点的先后，往往比处置本身更能影响公众感受。",
        ],
      },
    ],
    conclusion:
      "一条视频的消失，背后其实是一条很典型的传播链：起点克制、中转放大、回应滞后、处置补位。看清这条链，比争论它「该不该被撤下」更接近问题的核心。",
  },
  {
    id: "a04",
    slug: "studio-split-timeline",
    title: "从搭档到对簿公堂：一次工作室分家的时间线复盘",
    summary:
      "曾经共同出现在镜头前的两个人，为什么走到了法庭。我们只依据公开可查的时间点，把合作关系的裂痕标出来。",
    category: "娱乐观察",
    categorySlug: "entertainment",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20cream%20paper%20and%20two%20separating%20burgundy%20shapes%2C%20minimal%20split%20composition%20symbolising%20partnership%20breakup%2C%20soft%20studio%20light%2C%20flat%20graphic%20magazine%20style%2C%20no%20text&width=800&height=600&seq=art-04-studio-15&orientation=landscape",
    author: "编辑部 · 娱观组",
    date: "2026-08-18",
    readTime: "10 分钟",
    tags: ["工作室", "股权纠纷", "合作关系"],
    heat: 8020,
    featured: false,
    intro: [
      "他们最初是两个人在一间小工作室里做内容，分工很明确：一个负责出镜表达，一个负责幕后制作。凭借稳定的更新节奏和鲜明的风格，账号在两年内积累了可观的观众基础。",
      "关系的变化并不是某天突然发生的。公开信息里，至少有四个节点出现了「合作方式调整」的说法。把这些节点连起来看，会发现纠纷的种子在矛盾公开之前很早就已经埋下。",
    ],
    timeline: [
      {
        date: "第一年",
        title: "共同创立工作室",
        detail: "两人以口头约定的分工开始合作，账号定位与内容形式在这一年基本成型。",
      },
      {
        date: "第二年",
        title: "引入第三方，股权结构调整",
        detail: "工作室引入新的运营成员，公开信息显示股权结构做了调整，但具体比例未对外披露。",
      },
      {
        date: "第三年上半年",
        title: "出镜频率出现变化",
        detail: "其中一方在内容中出现的频率明显下降，同期更新节奏也开始不稳定。",
      },
      {
        date: "第三年下半年",
        title: "双方各自发布说明",
        detail: "两人先后发布声明，措辞都强调「尊重」与「感谢」，但均未说明分歧的具体内容。",
      },
      {
        date: "第四年",
        title: "进入法律程序",
        detail: "公开渠道出现相关法律程序的信息，涉及工作室股权与收益分配。",
      },
    ],
    keyFigures: [
      {
        name: "出镜一方",
        role: "内容前台",
        note: "在账号中承担主要表达功能，公众认知度更高，其诉求集中在账号归属与收益分配。",
      },
      {
        name: "制作一方",
        role: "内容幕后",
        note: "负责制作与运营，公开信息较少，纠纷的核心争议点与股权结构密切相关。",
      },
      {
        name: "第三方运营成员",
        role: "结构变量",
        note: "在第二年被引入，时间点早于两人分歧公开，是理解股权变化的关键角色。",
      },
    ],
    doubts: [
      {
        question: "最初的口头约定，为什么没能兜住后来的分歧？",
        analysis:
          "合作早期靠信任运转是常见的，但当账号变成有明确商业价值的资产，口头约定就无法回答「谁占多少」这个具体问题。分歧不是信任消失，而是信任之外缺少可执行的条款。",
      },
      {
        question: "引入第三方是否改变了原有的平衡？",
        analysis:
          "第三方进入的时间点，明显早于两人分歧公开。它未必是矛盾的起因，但确实改变了原有的决策结构，使原本两个人能商量的分歧变成了需要投票的问题。",
      },
      {
        question: "两人的公开说明为什么都如此克制？",
        analysis:
          "措辞克制通常意味着争议仍在进行中，任何过强的表达都可能影响后续程序。这本身也是纠纷已经进入正式阶段的一个信号。",
      },
    ],
    analysis: [
      {
        heading: "纠纷的核心从来不是感情，而是资产",
        paragraphs: [
          "把时间线拉长看，分歧的起点并不是某次争吵，而是账号从「作品」变成「资产」的那一刻。作品可以共享，资产必须确权，而确权需要的是当初没有写下的条款。",
        ],
      },
      {
        heading: "对创作合伙的三点提醒",
        paragraphs: [
          "第一，分工可以口头，但分配要落到纸面；第二，引入新成员时，要同步更新决策规则；第三，越是要好的搭档，越应该在关系最好的时候把规则谈清楚。",
        ],
      },
    ],
    conclusion:
      "这不是一个「谁对谁错」的故事，而是一个关于确权的故事。当合作关系里的资产开始产生明确价值，没有条款保护的信任，反而成了最容易被消耗的东西。",
  },
  {
    id: "a05",
    slug: "ai-face-swap-privacy",
    title: "AI 换脸争议：技术狂欢下的隐私灰区",
    summary:
      "一项已经成熟到可以随手使用的技术，为什么总在同一个环节引发争议？我们复盘了最近一轮讨论的来龙去脉。",
    category: "科技前沿",
    categorySlug: "tech",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20paper%20texture%20and%20amber%20geometric%20face%20grid%2C%20minimal%20investigative%20magazine%20mood%2C%20soft%20studio%20lighting%2C%20flat%20collage%20composition%20with%20crimson%20lines%2C%20no%20text&width=800&height=600&seq=art-05-ai-15&orientation=landscape",
    author: "编辑部 · 科技组",
    date: "2026-08-12",
    readTime: "9 分钟",
    tags: ["AI 换脸", "隐私边界", "平台责任"],
    heat: 7780,
    featured: false,
    intro: [
      "换脸并不是新技术，它真正进入公众讨论是在工具足够简单之后：一个普通人只需要几张照片和几分钟，就能生成一段几乎以假乱真的视频。门槛的降低，让争议从技术圈扩散到了日常。",
      "最近一轮讨论的导火索，是某平台上出现的一批换脸内容被集中下架。下架本身并不意外，值得追问的是：这类内容的边界到底应该划在哪里，以及谁来划。",
    ],
    timeline: [
      {
        date: "第 1 周",
        title: "工具门槛进一步降低",
        detail: "面向普通用户的换脸工具更新，操作流程简化到数步，生成时间缩短到分钟级。",
      },
      {
        date: "第 2 周",
        title: "相关内容大量涌现",
        detail: "平台上集中出现一批换脸视频，内容以娱乐为主，但涉及未经授权的肖像使用。",
      },
      {
        date: "第 3 周",
        title: "集中下架与规则更新",
        detail: "平台对相关内容做集中处理，并更新了生成式内容的标识规则。",
      },
      {
        date: "第 3 周末",
        title: "讨论转向规则本身",
        detail: "讨论焦点从个别视频转向「标识是否足够」「授权如何界定」等机制问题。",
      },
    ],
    keyFigures: [
      {
        name: "工具提供方",
        role: "技术供给方",
        note: "决定生成门槛的高低，其是否要求授权确认，直接影响内容池的整体形态。",
      },
      {
        name: "平台内容治理",
        role: "规则执行方",
        note: "以标识与下架为主要手段，但标识本身并不能解决「是否获得授权」的问题。",
      },
      {
        name: "被生成内容的当事人",
        role: "权益受影响方",
        note: "在事件中通常缺乏主动知情渠道，往往在内容已经传播后才被动发现。",
      },
    ],
    doubts: [
      {
        question: "给内容加上标识，就足够了吗？",
        analysis:
          "标识解决的是「是否知道这是生成的」，但没解决「是否被允许生成」。前者是透明度问题，后者是授权问题，两者不能互相替代。",
      },
      {
        question: "授权从哪里来？",
        analysis:
          "肖像的使用授权通常需要明确的、可追溯的同意。工具侧的勾选确认，在多大程度上构成有效授权，目前尚无清晰共识，这是争议的核心缺口。",
      },
      {
        question: "为什么当事人总是最后知道？",
        analysis:
          "发现依赖被动渠道，是这类问题的共同特征。在缺乏主动监测机制的前提下，处置永远是滞后的。",
      },
    ],
    analysis: [
      {
        heading: "争议不在技术，在技术之外的三个空位",
        paragraphs: [
          "第一是授权空位：生成之前缺少有效的许可环节；第二是发现空位：当事人在内容传播前难以知情；第三是追责空位：责任在工具、发布者与平台之间流动，缺少清晰的落点。",
          "技术本身是中性的，它的社会影响取决于这三个空位有没有被填上。",
        ],
      },
      {
        heading: "把讨论从「该不该用」转向「怎么用」",
        paragraphs: [
          "简单地反对或拥护一项技术，通常解决不了问题。更可行的路径是明确使用边界：在什么场景下需要书面授权，标识应该出现在什么位置，争议发生时的处理时限是多久。",
        ],
      },
    ],
    conclusion:
      "换脸争议的本质，是技术的使用速度跑在了规则前面。它的解法不会是禁掉工具，而更可能是把授权、标识与追责这三件事，一件一件补上。",
  },
  {
    id: "a06",
    slug: "teacher-viral-and-town-school",
    title: "支教老师走红之后：流量如何改变一所县城中学",
    summary:
      "一段支教日常视频意外走红，让一所县中学突然进入公众视野。流量带来了关注，也带来了原本不存在的问题。",
    category: "社会话题",
    categorySlug: "society",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20sand%20and%20cream%20tones%2C%20abstract%20school%20window%20geometry%20and%20soft%20crimson%20line%2C%20neutral%20documentary%20magazine%20mood%2C%20soft%20daylight%2C%20flat%20graphic%20style%2C%20no%20text&width=800&height=600&seq=art-06-teacher-15&orientation=landscape",
    author: "编辑部 · 社会组",
    date: "2026-08-05",
    readTime: "10 分钟",
    tags: ["支教", "流量冲击", "县城教育"],
    heat: 7450,
    featured: false,
    intro: [
      "视频本身很日常：一间朴素的教室、一块擦了又写的黑板、一个讲题讲到一半停下来喝水的人。它之所以被大量转发，恰恰是因为它「不够精致」。",
      "热度来得很快。一个月内，这所学校收到了远超以往的关注，也收到了远超以往的打扰。我们把这段时间的变化按顺序记录下来，看到的是流量进入一个原本安静系统之后，会产生的连锁反应。",
    ],
    timeline: [
      {
        date: "第 1 周",
        title: "日常视频意外走红",
        detail: "一段课堂片段被大量转发，账号粉丝数在数天内快速增长。",
      },
      {
        date: "第 2 周",
        title: "关注转化为实际帮扶",
        detail: "出现物资捐赠与图书寄送，学校收到首批来自陌生网友的支持。",
      },
      {
        date: "第 3 周",
        title: "访问需求开始增加",
        detail: "陆续有个人与团队希望到校参观或拍摄，正常的教学秩序开始受到影响。",
      },
      {
        date: "第 5 周",
        title: "发布者主动降低更新频率",
        detail: "账号更新明显放缓，公开发声希望外界减少对教学现场的打扰。",
      },
      {
        date: "第 8 周",
        title: "讨论热度回落",
        detail: "关注度下降，学校收到的一次性帮扶也相应减少，日常教学基本恢复。",
      },
    ],
    keyFigures: [
      {
        name: "支教老师",
        role: "流量起点",
        note: "原本只想记录工作日常，走红后成为承接外部关注的唯一窗口，承受了大部分沟通压力。",
      },
      {
        name: "学校管理层",
        role: "秩序维护方",
        note: "需要在接受帮扶与维持教学秩序之间做平衡，是访问需求的主要协调者。",
      },
      {
        name: "关注者群体",
        role: "外部变量",
        note: "善意与打扰常常同时到达，缺乏统一渠道是问题被放大的原因之一。",
      },
    ],
    doubts: [
      {
        question: "帮扶为什么往往来得快、走得也快？",
        analysis:
          "一次性物资与持续关注是两种完全不同的支持。热度驱动的是前者，它不需要长期承诺，因此会随着关注度自然衰减。",
      },
      {
        question: "教学秩序受影响，责任在谁？",
        analysis:
          "很难归因到某一方。发布者没有预料到热度，访问者带着善意，学校管理层缺少应对经验。更接近事实的解释是：缺少一个能承接关注的中间机制。",
      },
      {
        question: "热度退潮之后，学校留下了什么？",
        analysis:
          "公开信息显示留下的是部分物资与一群好奇过的人。真正可持续的支持，通常需要制度化的渠道，而不是一次性的情绪。",
      },
    ],
    analysis: [
      {
        heading: "流量对一个安静系统的冲击，是可预期的",
        paragraphs: [
          "一所县中学的日常运转，依赖的是稳定而非关注。当大量外部关注在短时间内涌入，最先被消耗的就是这种稳定性：老师要接待，学生要适应镜头，管理层要学处理舆情。",
        ],
      },
      {
        heading: "善意需要被组织，才能真正落地",
        paragraphs: [
          "这段经历最有价值的地方，是它展示了善意与组织之间的差距。物资不缺，难的是把它持续、有序地送到需要的地方，并且不打扰原来的节奏。",
        ],
      },
    ],
    conclusion:
      "流量来过，也散去了。它留下的问题比答案更多：当公众的善意突然涌向一个安静的地方，我们要怎样既不辜负这份善意，也不打乱那里本来的生活。",
  },
  {
    id: "a07",
    slug: "endorsement-contract-three-pages",
    title: "品牌代言解约风波：合同里被忽略的那三页",
    summary:
      "一次代言合作在公开前被终止，双方都提到了「条款」。我们不去猜谁对谁错，只梳理这类合作里最容易出问题的环节。",
    category: "娱乐观察",
    categorySlug: "entertainment",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20cream%20paper%20and%20burgundy%20contract%20document%20geometry%20and%20amber%20seal%20shape%2C%20minimal%20investigative%20magazine%20mood%2C%20soft%20studio%20light%2C%20flat%20collage%20style%2C%20no%20text&width=800&height=600&seq=art-07-contract-15&orientation=landscape",
    author: "编辑部 · 娱观组",
    date: "2026-07-28",
    readTime: "8 分钟",
    tags: ["代言", "合同条款", "解约"],
    heat: 6990,
    featured: false,
    intro: [
      "一场已经拍完物料、准备官宣的代言合作，在公开前一周被终止。双方的对外说明都提到了「条款」，但都没有展开。这类事件最容易被讨论成道德问题，而实际的争议点往往藏在合同的细节里。",
      "我们没有第一手合同，也不会去推测具体条款。能做的，是把这类合作中公开信息反复指向的几个脆弱环节梳理清楚，帮助读者理解为什么「谈好的事」会在最后时刻发生变化。",
    ],
    timeline: [
      {
        date: "T-45 天",
        title: "双方达成合作意向",
        detail: "合作框架敲定，进入物料拍摄与档期协调阶段。",
      },
      {
        date: "T-20 天",
        title: "完成素材拍摄",
        detail: "广告素材拍摄完成，进入后期与排期环节。",
      },
      {
        date: "T-10 天",
        title: "出现外部变量",
        detail: "合作相关方在社交平台出现争议性话题，被品牌方纳入风险评估。",
      },
      {
        date: "T-7 天",
        title: "合作终止",
        detail: "原定的官宣被取消，双方各自发布简短说明，均提及条款与协商。",
      },
      {
        date: "T+5 天",
        title: "物料下架与沉默",
        detail: "已拍摄素材未再公开，双方均未进一步说明终止的具体条款依据。",
      },
    ],
    keyFigures: [
      {
        name: "品牌市场团队",
        role: "风险决策方",
        note: "负责评估合作风险，其判断依据通常是合同中的形象维护条款，而非事件本身的是非。",
      },
      {
        name: "艺人经纪团队",
        role: "权益主张方",
        note: "关注已投入的档期与工作量是否获得对应补偿，问题聚焦在履行的边界上。",
      },
      {
        name: "法务对接人",
        role: "条款解释方",
        note: "双方争议最终会落到条款的表述是否明确，但相关内容通常不对外公开。",
      },
    ],
    doubts: [
      {
        question: "「形象维护条款」到底是怎么写的？",
        analysis:
          "这类条款的核心模糊点通常在于「什么程度的事件触发解约权」。写得越笼统，执行时越容易产生分歧，而公众往往看不到这一层。",
      },
      {
        question: "已经拍摄的物料和投入怎么算？",
        analysis:
          "物料成本与档期占用的归属，取决于合同中关于「非违约终止」的约定。公开信息没有说明终止被归为哪一类，因此补偿问题无从判断。",
      },
      {
        question: "为什么双方都只提「条款」不说内容？",
        analysis:
          "保密条款是这类合作的标配。措辞越模糊，越说明争议可能进入了协商阶段，双方都在为后续谈判保留空间。",
      },
    ],
    analysis: [
      {
        heading: "代言合作的真正风险点，在签约那一刻",
        paragraphs: [
          "多数人以为风险在执行阶段，实际上风险在签约时就埋下了。触发条件是否具体、补偿方式是否明确、非违约终止如何计算，这三件事在签约时谈清楚，执行阶段的分歧就会少很多。",
        ],
      },
      {
        heading: "公众看到的，只是争议的最后一层",
        paragraphs: [
          "一件代言终止，公众能看到的通常只有两份简短说明。真正决定结果的谈判、条款与补偿方案，都发生在看不到的地方。这也是这类事件几乎不会有「明确结论」的原因。",
        ],
      },
    ],
    conclusion:
      "与其追问谁对谁错，不如记住一个更普遍的规律：合作越是被情绪驱动，条款越需要被写清楚。真正保护双方的，从来不是口头承诺，而是那些没人愿意细读的三页纸。",
  },
  {
    id: "a08",
    slug: "vegetable-price-five-questions",
    title: "菜价为何突然翻倍：一条供应链上的五个问号",
    summary:
      "同一批蔬菜，从产地到菜市场价格翻了一倍。我们把中间环节一段一段拆开，标出价格真正上涨的位置。",
    category: "社会话题",
    categorySlug: "society",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20sand%20and%20cream%20paper%20tones%2C%20abstract%20produce%20crate%20geometry%20and%20crimson%20supply%20line%2C%20clean%20investigative%20magazine%20mood%2C%20soft%20daylight%2C%20flat%20graphic%20style%2C%20no%20text&width=800&height=600&seq=art-08-supply-15&orientation=landscape",
    author: "编辑部 · 社会组",
    date: "2026-07-20",
    readTime: "9 分钟",
    tags: ["菜价", "供应链", "成本结构"],
    heat: 6330,
    featured: false,
    intro: [
      "同一批蔬菜，产地收购价和终端零售价之间差了一倍还不止。这种情况在极端天气或节假日前后并不罕见，但「为什么涨」这个问题，很少被真正拆开来看。",
      "与其讨论「是不是有人在炒」，不如把从产地到餐桌的环节一段段拆开。价格往往不是在某一处突然翻倍，而是在多个环节被一点点加上去的。",
    ],
    timeline: [
      {
        date: "环节一",
        title: "产地收购",
        detail: "受天气影响，同期上市量减少，收购价出现上行，这是价格变化的起点。",
      },
      {
        date: "环节二",
        title: "短途集散与分级",
        detail: "运输、分级、包装产生固定成本，这部分在价格中占比相对稳定。",
      },
      {
        date: "环节三",
        title: "冷链与长途运输",
        detail: "保鲜要求高的品类，在高温季节的损耗率明显上升，推高单位成本。",
      },
      {
        date: "环节四",
        title: "批发市场",
        detail: "批发环节的加价通常有限，但对损耗的补偿会体现在报价上。",
      },
      {
        date: "环节五",
        title: "终端零售",
        detail: "零售端需要覆盖摊位、人工与滞销损耗，这部分加价幅度不容易被直观感知。",
      },
    ],
    keyFigures: [
      {
        name: "产地收购商",
        role: "价格起点",
        note: "收购价随供求波动，是整个链条中弹性最大的一环。",
      },
      {
        name: "物流承运方",
        role: "成本变量",
        note: "高温季节的保鲜损耗难以预测，是终端价格波动的重要来源。",
      },
      {
        name: "终端摊主",
        role: "价格呈现者",
        note: "承担滞销损耗，其加价中有相当一部分是对损耗的预补偿。",
      },
    ],
    doubts: [
      {
        question: "翻倍的部分到底加在哪一环？",
        analysis:
          "公开的成本结构显示，产地与终端是波动最明显的两段。但具体比例因品类、季节而异，很难用一个统一数字概括，这是「翻倍」难以被简单归因的原因。",
      },
      {
        question: "中间环节是不是赚得太多？",
        analysis:
          "批发环节的加价通常在较低区间，真正被忽略的是损耗成本。高温季节一批蔬菜的滞销比例上升，会直接推高每单位的售价。",
      },
      {
        question: "为什么极端天气的影响会这么明显？",
        analysis:
          "叶菜类供应周期短，产量对天气的反应非常直接。供给在短时间内收缩，价格就会迅速上行，这是一个结构性问题而非人为操作。",
      },
    ],
    analysis: [
      {
        heading: "「翻倍」是多个小加价叠加的结果",
        paragraphs: [
          "每个环节单独看，加价幅度都不夸张：运输加一点，损耗补一点，零售再覆盖一点。但它们叠加起来，就形成了终端与产地之间那道看起来很夸张的差距。",
        ],
      },
      {
        heading: "想看懂菜价，先看损耗率",
        paragraphs: [
          "在生鲜链条里，损耗率往往比收购价更能解释终端价格。理解了这一点，也就能理解为什么同类蔬菜在不同季节的涨幅差异会那么大。",
        ],
      },
    ],
    conclusion:
      "菜价上涨很少有一个单一的原因，它更像是链条上每个环节各加了一点之后的结果。与其寻找「谁在炒」，不如把每一段成本摊开来看，答案反而更清楚。",
  },
  {
    id: "a09",
    slug: "open-source-fork-after-acquisition",
    title: "开源项目被收购之后，社区为何集体「出走」",
    summary:
      "一个被广泛使用的小工具被商业公司接手，几周后主要维护者集体另起分支。这是一次典型的分叉事件复盘。",
    category: "科技前沿",
    categorySlug: "tech",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20paper%20texture%20and%20amber%20branching%20line%20geometry%2C%20abstract%20forked%20path%20shapes%20in%20crimson%2C%20minimal%20investigative%20magazine%20mood%2C%20soft%20light%2C%20flat%20composition%2C%20no%20text&width=800&height=600&seq=art-09-opensource-15&orientation=landscape",
    author: "编辑部 · 科技组",
    date: "2026-07-12",
    readTime: "9 分钟",
    tags: ["开源", "分叉", "社区治理"],
    heat: 5880,
    featured: false,
    intro: [
      "那个项目很小：一段不到两千行的实用工具，作者维护了三年，在相关圈子里几乎人手一个。它的知名度不高，但依赖它的人很稳定。",
      "变化始于作者宣布以个人名义将项目转让给一家商业公司。几周后，项目的主要贡献者发起了一个独立分支。这个过程被外界简单概括为「社区叛逃」，但实际的分歧点要具体得多。",
    ],
    timeline: [
      {
        date: "第 1 周",
        title: "作者宣布转让项目",
        detail: "作者称因维护精力有限，决定将项目交由一家商业公司继续维护，承诺保持开源。",
      },
      {
        date: "第 2 周",
        title: "治理结构发生变化",
        detail: "新维护方调整了贡献者权限与合并流程，部分原有维护者权限被收回。",
      },
      {
        date: "第 3 周",
        title: "路线图出现分歧",
        detail: "新维护方公布的路线图中包含闭源的云端功能，引发关于边界的讨论。",
      },
      {
        date: "第 4 周",
        title: "主要贡献者发起分支",
        detail: "多名核心贡献者另起仓库，保留原有许可证与贡献流程，并邀请原社区迁移。",
      },
      {
        date: "第 6 周",
        title: "两个版本并行",
        detail: "原项目与分支同时维护，使用者在依赖选择上开始出现分化。",
      },
    ],
    keyFigures: [
      {
        name: "原作者",
        role: "转让决策方",
        note: "出发点是维护精力有限，但在转让时对治理结构的交接考虑不足。",
      },
      {
        name: "接手公司团队",
        role: "新维护方",
        note: "希望把项目与商业产品打通，其路线图与社区的开放预期出现了张力。",
      },
      {
        name: "核心贡献者群体",
        role: "社区代表",
        note: "对「开源边界」较为敏感，权限与路线图的叠加变化成为他们离开的直接原因。",
      },
    ],
    doubts: [
      {
        question: "如果作者有权转让，社区为什么反对？",
        analysis:
          "法律上的所有权与情感上的归属常常并不重合。项目虽然由作者发起，但它的价值很大程度上来自社区持续的贡献，转让忽略了这一层。",
      },
      {
        question: "争议的核心是闭源吗？",
        analysis:
          "路线图中的闭源部分是导火索，但更根本的是权限调整。治理结构的变化，让贡献者感到自己不再是项目的参与者，而只是使用者。",
      },
      {
        question: "分支之后，哪个版本会胜出？",
        analysis:
          "取决于使用者更在意什么。看重商业支持的一方会留下，看重开放治理的一方会迁移。这类结果通常由生态而非技术本身决定。",
      },
    ],
    analysis: [
      {
        heading: "开源的核心资产，是治理而不是代码",
        paragraphs: [
          "代码可以被复制，治理却很难。一次成功的转让，真正要交接的不是仓库，而是社区对决策流程的信任。权限与路线图同时变动，等于在一次动作里挑战了这份信任。",
        ],
      },
      {
        heading: "分叉不是失败，而是开源的一种常态",
        paragraphs: [
          "分叉意味着使用者有了选择，从生态角度看未必是坏事。真正需要反思的，是转让过程中能不能把治理规则一起写清楚，让分歧在更早的阶段被讨论。",
        ],
      },
    ],
    conclusion:
      "这次分叉留下的经验很朴素：开源项目可以换主人，但不能轻易换规则。一旦规则变化快于沟通，社区就会用脚投票，而分叉，正是它投票的方式。",
  },
  {
    id: "a10",
    slug: "franchise-expansion-cost",
    title: "加盟商维权实录：连锁餐饮扩张的代价",
    summary:
      "同一条街上三家门店关了两家，加盟商却发现自己签的合同里写满了「不保证」。我们把扩张逻辑层层拆开。",
    category: "商业风云",
    categorySlug: "business",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20abstract%20artwork%20with%20warm%20beige%20tones%20and%20burgundy%20shop%20grid%20geometry%2C%20abstract%20storefront%20row%20shapes%2C%20clean%20investigative%20magazine%20collage%2C%20soft%20daylight%2C%20flat%20graphic%20style%2C%20no%20text&width=800&height=600&seq=art-10-franchise-15&orientation=landscape",
    author: "编辑部 · 商业组",
    date: "2026-07-04",
    readTime: "11 分钟",
    tags: ["加盟", "扩张", "合同风险"],
    heat: 6120,
    featured: false,
    intro: [
      "在同一条不到八百米的商业街上，同一个连锁品牌曾经开出三家门店。两年后再去看，只剩下一家还亮着灯。这个比例，是很多加盟商真实处境的缩影。",
      "加盟是一种购买「确定性」的生意：总部提供品牌、供应链与运营经验，加盟商出资金和人力。但当扩张速度超过单店承载力，被稀释的恰恰是那份确定性。我们按公开信息梳理了这类模式的典型节点。",
    ],
    timeline: [
      {
        date: "招商阶段",
        title: "以样板店数据招揽加盟",
        detail: "招商材料以经营最好的样板店数据为参考，但通常不保证加盟店能达到相近水平。",
      },
      {
        date: "签约阶段",
        title: "合同侧重总部权益",
        detail: "保证金、装修指定、供应链采购等条款较多，关于经营风险的约定相对笼统。",
      },
      {
        date: "开业阶段",
        title: "密度扩张开始显现",
        detail: "同商圈内新店持续开出，门店之间开始分流客源，单店经营压力上升。",
      },
      {
        date: "经营阶段",
        title: "加盟商提出诉求",
        detail: "部分加盟商就区域保护与供货价格提出质疑，与总部的沟通出现分歧。",
      },
      {
        date: "争议阶段",
        title: "进入协商或法律程序",
        detail: "公开信息显示，部分加盟商选择协商退出，部分进入法律途径主张权益。",
      },
    ],
    keyFigures: [
      {
        name: "连锁总部",
        role: "模式设计方",
        note: "以规模化获得供应链与品牌收益，招商节奏主要由其掌控。",
      },
      {
        name: "加盟商群体",
        role: "资金承担方",
        note: "承担了绝大部分经营风险，却在合同中对风险的保护条款最少。",
      },
      {
        name: "区域招商代理",
        role: "中间促成方",
        note: "负责具体招商落地，其业绩与签约数量直接相关，容易放大预期。",
      },
    ],
    doubts: [
      {
        question: "为什么样板店的数据不能复制？",
        analysis:
          "样板店通常位于人流最优的位置，且享受最早的品牌红利。当门店密度上升，同样的经营能力在不同位置上会得到完全不同的结果，数据不可复制是结构性的。",
      },
      {
        question: "区域保护为什么常常落空？",
        analysis:
          "如果合同里对「保护范围」只做笼统表述，总部在扩张时就有解释空间。争议往往不是有没有保护，而是保护的范围没有量化。",
      },
      {
        question: "加盟商签合同时最容易忽略什么？",
        analysis:
          "大多数人关注的是加盟费与供货价，却忽略了退出条款与风险分担。真正决定损失上限的，恰恰是这两部分。",
      },
    ],
    analysis: [
      {
        heading: "加盟的本质，是一次风险的不对称转移",
        paragraphs: [
          "总部用规模换收入，加盟商用现金换品牌和方法。当门店密度成为竞争手段，风险就集中到了加盟商一侧。理解了这一点，就能理解为什么这类纠纷几乎总在扩张期集中爆发。",
        ],
      },
      {
        heading: "签约前必须问清楚的三件事",
        paragraphs: [
          "第一，区域保护的量化范围是多少；第二，同类门店的密度上限是多少；第三，退出时保证金与装修资产如何结算。把这三个数字写进合同，比听十次招商宣讲更有效。",
        ],
      },
    ],
    conclusion:
      "加盟本身不是陷阱，信息不对称才是。当招商材料只谈未来不谈风险，当合同只约束义务不约束密度，风险就已经被悄悄转移了。看清这一点，是每一个潜在加盟商最重要的一课。",
  },
];

export const getArticleBySlug = (slug: string): Article | undefined =>
  articles.find((item) => item.slug === slug);

export const getArticlesByCategory = (slug: string): Article[] =>
  articles.filter((item) => item.categorySlug === slug);

export const featuredArticles = articles.filter((item) => item.featured);

export const latestArticles = [...articles].sort(
  (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
);

export const hotArticles = [...articles].sort((a, b) => b.heat - a.heat);